import React, { useState, useMemo } from 'react';
import { X, Save, Stethoscope, User, Calendar, AlertCircle } from 'lucide-react';
import Select from 'react-select';
import { usePatients, useDoctors, useHospitals } from '../../hooks/useApi';

const ConsultationAddModal = ({ isOpen, onClose, onSave, loading }) => {
  const { data: patients = [], loading: patientsLoading } = usePatients();
  const { data: doctors = [], loading: doctorsLoading } = useDoctors();
  const { data: hospitals = [], loading: hospitalsLoading } = useHospitals();

  const [formData, setFormData] = useState({
    patientId: null,
    doctorId: null,
    hospitalId: null,
    consultationDate: new Date().toISOString().slice(0, 16),
    chiefComplaint: '',
    symptoms: [],
    diagnosis: '',
    bloodPressure: '',
    heartRate: '',
    temperature: '',
    weight: '',
    height: '',
    status: 'En attente',
    priority: 'Normale',
    followUpRequired: false,
    followUpDate: '',
    notes: ''
  });

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState({ value: 'En attente', label: 'En attente' });
  const [selectedPriority, setSelectedPriority] = useState({ value: 'Normale', label: 'Normale' });
  const [errors, setErrors] = useState({});

  // Formatage des options pour les patients
  const patientOptions = useMemo(() => {
    if (!Array.isArray(patients) || patients.length === 0) {
      return [];
    }
    return patients
      .filter(patient => patient.user)
      .map(patient => {
        const name = patient.user?.name || 'Patient';
        const age = patient.age || 'N/A';
        return {
          value: patient.id,
          label: `${name} - ${age} ans`
        };
      });
  }, [patients]);

  // Formatage des options pour les médecins
  const doctorOptions = useMemo(() => {
    if (!Array.isArray(doctors) || doctors.length === 0) {
      return [];
    }
    return doctors
      .filter(doctor => doctor.user)
      .map(doctor => {
        const name = doctor.user.name || 'Médecin';
        const speciality = doctor.specialty || doctor.speciality || 'Non spécifié';
        return {
          value: doctor.id,
          label: `Dr. ${name} - ${speciality}`
        };
      });
  }, [doctors]);

  // Formatage des options pour les hôpitaux
  const hospitalOptions = useMemo(() => {
    if (!Array.isArray(hospitals) || hospitals.length === 0) {
      return [];
    }
    return hospitals.map(hospital => ({
      value: hospital.id,
      label: `${hospital.name} - ${hospital.city}`
    }));
  }, [hospitals]);

  const statusOptions = [
    { value: 'En attente', label: 'En attente' },
    { value: 'En cours', label: 'En cours' },
    { value: 'Terminée', label: 'Terminée' },
    { value: 'Annulée', label: 'Annulée' },
    { value: 'Reportée', label: 'Reportée' }
  ];

  const priorityOptions = [
    { value: 'Faible', label: 'Faible' },
    { value: 'Normale', label: 'Normale' },
    { value: 'Élevée', label: 'Élevée' },
    { value: 'Urgente', label: 'Urgente' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePatientChange = (selectedOption) => {
    setSelectedPatient(selectedOption);
    setFormData(prev => ({ ...prev, patientId: selectedOption ? selectedOption.value : null }));
    if (errors.patientId) setErrors(prev => ({ ...prev, patientId: '' }));
  };

  const handleDoctorChange = (selectedOption) => {
    setSelectedDoctor(selectedOption);
    setFormData(prev => ({ ...prev, doctorId: selectedOption ? selectedOption.value : null }));
    if (errors.doctorId) setErrors(prev => ({ ...prev, doctorId: '' }));
  };

  const handleHospitalChange = (selectedOption) => {
    setSelectedHospital(selectedOption);
    setFormData(prev => ({ ...prev, hospitalId: selectedOption ? selectedOption.value : null }));
  };

  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    setFormData(prev => ({ ...prev, status: selectedOption ? selectedOption.value : 'En attente' }));
  };

  const handlePriorityChange = (selectedOption) => {
    setSelectedPriority(selectedOption);
    setFormData(prev => ({ ...prev, priority: selectedOption ? selectedOption.value : 'Normale' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.patientId) newErrors.patientId = 'Le patient est requis';
    if (!formData.doctorId) newErrors.doctorId = 'Le médecin est requis';
    if (!formData.chiefComplaint?.trim()) newErrors.chiefComplaint = 'Le motif de consultation est requis';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Nettoyer les données avant l'envoi
      const cleanedData = {
        patientId: formData.patientId,
        doctorId: formData.doctorId,
        consultationDate: formData.consultationDate,
        chiefComplaint: formData.chiefComplaint,
        status: formData.status,
        priority: formData.priority,
        followUpRequired: formData.followUpRequired
      };

      // Ajouter l'hôpital seulement s'il est sélectionné
      if (formData.hospitalId) {
        cleanedData.hospitalId = formData.hospitalId;
      }

      // Ajouter les autres champs seulement s'ils ne sont pas vides
      if (formData.diagnosis?.trim()) {
        cleanedData.diagnosis = formData.diagnosis;
      }
      if (formData.bloodPressure?.trim()) {
        cleanedData.bloodPressure = formData.bloodPressure;
      }
      if (formData.heartRate?.trim()) {
        cleanedData.heartRate = parseInt(formData.heartRate);
      }
      if (formData.temperature?.trim()) {
        cleanedData.temperature = parseFloat(formData.temperature);
      }
      if (formData.weight?.trim()) {
        cleanedData.weight = parseFloat(formData.weight);
      }
      if (formData.height?.trim()) {
        cleanedData.height = parseFloat(formData.height);
      }
      if (formData.followUpDate?.trim()) {
        cleanedData.followUpDate = formData.followUpDate;
      }
      if (formData.notes?.trim()) {
        cleanedData.notes = formData.notes;
      }

      onSave(cleanedData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-cyan-100 rounded-full">
              <Stethoscope className="h-6 w-6 text-cyan-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Nouvelle Consultation</h2>
              <p className="text-gray-600">Enregistrer une nouvelle consultation médicale</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" disabled={loading}>
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Patient et Médecin */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations générales</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Patient *</label>
                <Select
                  value={selectedPatient}
                  onChange={handlePatientChange}
                  options={patientOptions}
                  isLoading={patientsLoading}
                  placeholder="Sélectionner un patient..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {errors.patientId && <p className="text-red-500 text-sm mt-1">{errors.patientId}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Médecin *</label>
                <Select
                  value={selectedDoctor}
                  onChange={handleDoctorChange}
                  options={doctorOptions}
                  isLoading={doctorsLoading}
                  placeholder="Sélectionner un médecin..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {errors.doctorId && <p className="text-red-500 text-sm mt-1">{errors.doctorId}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hôpital</label>
                <Select
                  value={selectedHospital}
                  onChange={handleHospitalChange}
                  options={hospitalOptions}
                  isLoading={hospitalsLoading}
                  isClearable
                  placeholder="Sélectionner un hôpital..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date et heure *</label>
                <input
                  type="datetime-local"
                  name="consultationDate"
                  value={formData.consultationDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
            </div>
          </div>

          {/* Motif et Diagnostic */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Consultation médicale</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Motif de consultation *</label>
                <input
                  type="text"
                  name="chiefComplaint"
                  value={formData.chiefComplaint}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${errors.chiefComplaint ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Ex: Douleur abdominale, fièvre..."
                />
                {errors.chiefComplaint && <p className="text-red-500 text-sm mt-1">{errors.chiefComplaint}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Diagnostic</label>
                <input
                  type="text"
                  name="diagnosis"
                  value={formData.diagnosis}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Ex: Gastro-entérite"
                />
              </div>
            </div>
          </div>

          {/* Signes vitaux */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Signes vitaux</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tension artérielle</label>
                <input
                  type="text"
                  name="bloodPressure"
                  value={formData.bloodPressure}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Ex: 120/80"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rythme cardiaque</label>
                <input
                  type="number"
                  name="heartRate"
                  value={formData.heartRate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="BPM"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Température (°C)</label>
                <input
                  type="number"
                  step="0.1"
                  name="temperature"
                  value={formData.temperature}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="°C"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Poids (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  name="weight"
                  value={formData.weight}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="kg"
                />
              </div>
            </div>
          </div>

          {/* Statut et Priorité */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Suivi</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                <Select
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  options={statusOptions}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
                <Select
                  value={selectedPriority}
                  onChange={handlePriorityChange}
                  options={priorityOptions}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="followUpRequired"
                    checked={formData.followUpRequired}
                    onChange={handleChange}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Suivi requis</span>
                </label>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Notes additionnelles..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center space-x-2 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConsultationAddModal;
